import type { Role } from "./types/entities"

interface RouteAccess {
  role: Role
  can: (permission: string) => boolean
  /** Roles with a scopePolicy use the current permission model. */
  targetModel: boolean
  /** A legacy role with users:write. */
  legacyAdmin: boolean
}

type RouteRule = (access: RouteAccess) => boolean

const always: RouteRule = () => true

const permission =
  (name: string): RouteRule =>
  ({ can }) =>
    can(name)

/** The permission for current roles; legacy roles need users:write. */
const permissionOrLegacyAdmin =
  (name: string): RouteRule =>
  ({ can, targetModel, legacyAdmin }) =>
    targetModel ? can(name) : legacyAdmin

const activityList: RouteRule = ({ can, targetModel, legacyAdmin }) =>
  can("activities:read") ||
  (!targetModel && (can("tasks:read") || legacyAdmin || can("teams:manage")))

const activityAdministration: RouteRule = ({ role, can, targetModel }) =>
  targetModel
    ? role.activityScope === "admin" && can("activities:read")
    : can("tasks:write")

const userDirectory: RouteRule = ({ can, targetModel, legacyAdmin }) =>
  can("users:read") || (!targetModel && (legacyAdmin || can("teams:manage")))

const personDirectory: RouteRule = ({ can, targetModel }) =>
  can("persons:read") || (!targetModel && can("persons:write"))

const teamManagement: RouteRule = ({ can, targetModel, legacyAdmin }) =>
  targetModel ? can("teams:read") : legacyAdmin || can("teams:manage")

const roleManagement: RouteRule = ({ can, targetModel }) =>
  targetModel ? can("roles:read") : can("roles:manage")

const hoursReport: RouteRule = ({ can, targetModel }) =>
  can("billing:read") ||
  (targetModel && (can("reports:read-summary") || can("hours:read")))

const activityImport: RouteRule = ({ role, can, targetModel, legacyAdmin }) =>
  targetModel
    ? role.activityScope === "admin" && can("activities:create")
    : legacyAdmin && can("tasks:write")

const notificationSettings: RouteRule = ({ can, targetModel, legacyAdmin }) =>
  targetModel ? can("settings:update") : legacyAdmin && can("settings:write")

const ROUTE_RULES = new Map<string, RouteRule>([
  ["/", activityList],
  ["/activities", activityAdministration],
  ["/my-relations", always],
  ["/admin/users", userDirectory],
  ["/admin/persons", personDirectory],
  ["/admin/teams", teamManagement],
  ["/admin/roles", roleManagement],
  ["/report", hoursReport],
  ["/admin/organization-hours", permission("organization-hours:read")],
  ["/admin/importer", activityImport],
  ["/admin/spond-import", permissionOrLegacyAdmin("users:import")],
  ["/admin/calendar-sync", permissionOrLegacyAdmin("activityTypes:manage")],
  ["/admin/activity-types", permissionOrLegacyAdmin("activityTypes:manage")],
  [
    "/admin/activity-templates",
    permissionOrLegacyAdmin("activityTypes:manage"),
  ],
  ["/admin/annual-hours", permissionOrLegacyAdmin("settings:read")],
  ["/admin/notifications", notificationSettings],
  ["/billing/settings", permissionOrLegacyAdmin("billing:manage")],
  ["/billing/settings/callback", permissionOrLegacyAdmin("billing:manage")],
  ["/billing/invoices", permissionOrLegacyAdmin("billing:read")],
  ["/billing/accountant-export", permission("billing:export")],
  ["/admin/campaigns/settings", permission("campaigns:settings")],
  ["/admin/automation", permission("automation:manage")],
  ["/admin/campaigns", permission("campaigns:send")],
  ["/admin/campaigns/email", permission("campaigns:send")],
  ["/admin/campaigns/email/:campaignId/report", permission("campaigns:send")],
  ["/admin/campaigns/sms", permission("campaigns:send")],
  ["/admin/campaigns/sms/:campaignId/report", permission("campaigns:send")],
])

/** Visibility only. Rules and commands independently authorize every operation. */
export const canAccessTenantRoute = (
  path: string,
  role: Role | null | undefined,
  can: (permission: string) => boolean,
  platformAdministrator = false,
): boolean => {
  if (platformAdministrator) return true
  if (!role) return path === "/my-relations"
  const rule = ROUTE_RULES.get(path)
  if (!rule) return false
  const targetModel = Object.hasOwn(role, "scopePolicy")
  return rule({
    role,
    can,
    targetModel,
    legacyAdmin: !targetModel && can("users:write"),
  })
}
